import React, { useState } from "react";
import {
  generatePath,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import "./GameLandingPage.scss";
import GameMediaPreview from "../../components/game-play/GameMediaPreview/GameMediaPreview";
import LatestWinsSlider from "../../components/onboarding/LatestWinsSlider/LatestWinsSlider";
import PrimaryButtonWithBonusTag from "../../components/onboarding/PrimaryButtonWithBonusTag/PrimaryButtonWithBonusTag";
import PlayIconFlilled from "../../components/icons/PlayIconFlilled";
import ImageHyperLogo from "../../images/hyper-full-white.svg";
import useAuth from "../../hooks/useAuth";
import useOnboardingSteps from "../../hooks/useOnboardingSteps";
import { useGameMediaSelection } from "../../hooks/useGamesPage";
import useGameCategoriesQuery from "../../react-query/queries/use-game-categories";
import useGameDetailsBySlugQuery from "../../react-query/queries/use-game-details-by-slug";
import useHprPurchasePlansQuery from "../../react-query/queries/use-hpr-purchase-plans";
import { AuthFlow, OnboardingSteps, ROUTES } from "../../utils/constants";
import {
  setStoredAuthFlow,
  setStoredPostOnboardingRedirect,
} from "../../utils/storage";
import {
  REDIRECT_QUERY_PARAM,
  omitQueryParams,
  resolveRedirectAlias,
} from "../../utils/helpers";
import { renderHeaders } from "../../utils/renderHeaders";
import NotFound from "../NotFound/NotFound";
import {
  FaqSection,
  GenrePills,
  HowItWorksSection,
  SafetySection,
  StatsSection,
  WelcomeBonusSection,
} from "./components/GameLandingPageSections";
import MoreGamesSection from "./components/MoreGamesSection";
import WinnersCarousel from "./components/WinnersCarousel";

export default function GameLandingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation(["games", "common", "metadata"]);
  const { isLoggedIn } = useAuth();
  const { goToStep } = useOnboardingSteps();
  const [isContinuing, setIsContinuing] = useState(false);
  const { hasWelcomeBonus, welcomeBonusPercent } =
    useHprPurchasePlansQuery();
  const showWelcomeBonus = isLoggedIn ? hasWelcomeBonus : true;
  const {
    data: gameDetails,
    isError: isGameMissing,
    isFetched,
  } = useGameDetailsBySlugQuery({
    slug: slug ?? "",
    enabled: !!slug,
    retry: false,
  });
  const { data: gameCategories } = useGameCategoriesQuery({
    enabled: true,
    shouldFetchAll: true,
  });
  const selectedMediaIndex = useGameMediaSelection({
    gameId: gameDetails?.id,
    gameMedia: gameDetails?.media,
  });

  if (!slug || isGameMissing) {
    return <NotFound />;
  }

  if (!gameDetails) {
    return isFetched ? (
      <NotFound />
    ) : (
      <main className="GameLandingPage" aria-busy="true" />
    );
  }

  const selectedMediaUrl =
    gameDetails.media && selectedMediaIndex !== undefined
      ? gameDetails.media[selectedMediaIndex]?.fileKey ?? ""
      : "";
  const gameName = gameDetails.referenceName || gameDetails.name;
  const redirectAlias = resolveRedirectAlias(
    new URLSearchParams(location.search).get(REDIRECT_QUERY_PARAM)
  );
  const continuePath =
    redirectAlias ??
    `${ROUTES.GAMES.ROOT}/${slug}${ROUTES.GAMES.PLAY_GAME}`;

  const handleContinue = () => {
    if (isContinuing) return;

    setIsContinuing(true);
    setStoredPostOnboardingRedirect(continuePath);

    if (!isLoggedIn) {
      const remainingSearch = omitQueryParams(location.search, [
        REDIRECT_QUERY_PARAM,
      ]);
      setStoredAuthFlow(AuthFlow.SIGNUP);
      goToStep(OnboardingSteps.SET_EARNINGS_GOALS);
      navigate(`${ROUTES.ROOT.SIGN_UP}${remainingSearch}`);
      return;
    }

    navigate(continuePath);
  };

  const handleSelectGame = (nextSlug: string) => {
    const nextPath = generatePath(ROUTES.GAMES.GAME_LANDING_PAGE, {
      slug: nextSlug,
    });

    navigate(`${nextPath}${location.search}`);
  };

  const handleViewMore = () => {
    if (isLoggedIn) {
      navigate(ROUTES.GAMES.ROOT);
      return;
    }

    handleContinue();
  };

  return (
    <main className="GameLandingPage">
      {renderHeaders({
        title: t("metadata:play_games_like_game_hyper_gaming_app", {
          gameName,
        }),
        description: t("metadata:play_game_1v1_multiplayer_seo_description", {
          gameName,
        }),
      })}
      <header className="nav">
        <img src={ImageHyperLogo} alt="Hyper" width={190} height={32} />
      </header>
      <section className="hero">
        <div className="hero-content">
          <LatestWinsSlider className="latest-win" />

          <div className="headline">
            <h1>
              <span>{t("game_landing.play_games_like")}</span>
              <mark>{gameName}</mark>
              <span>{t("game_landing.win_real_money")}</span>
            </h1>
            <p>
              <Trans
                ns="games"
                i18nKey="game_landing.description"
                components={{ br: <br /> }}
              />
            </p>
          </div>

          <div className="fixed-cta">
            <PrimaryButtonWithBonusTag
              size="lg"
              text={t("common:onboarding.start_playing")}
              textClassName="button-text"
              leftImage={<PlayIconFlilled width={16} height={16} fill="#fff" />}
              onClick={handleContinue}
              disabled={isContinuing}
              showBonusTag={showWelcomeBonus}
            />
          </div>
        </div>

        <GameMediaPreview
          key={`${gameDetails.id}-${selectedMediaUrl}`}
          gameName={gameName}
          poster={gameDetails.poster}
          mediaUrl={selectedMediaUrl}
          frameClassName="media-frame"
        />
      </section>

      <section className="stats-and-genres">
        <GenrePills gameCategories={gameCategories} />
        <StatsSection />
      </section>
      <WinnersCarousel />
      <HowItWorksSection />
      {showWelcomeBonus ? (
        <WelcomeBonusSection
          bonusPercent={welcomeBonusPercent}
          onClaim={handleContinue}
          disabled={isContinuing}
        />
      ) : null}
      <MoreGamesSection
        onSelectGame={handleSelectGame}
        onViewMore={handleViewMore}
      />
      <section className="faq-and-safety">
        <FaqSection />
        <SafetySection />
      </section>
    </main>
  );
}
